import api from '../../lib/api';
import type {
  CreateWorkbookCommentPayload,
  CreateWorkbookPayload,
  ImportWorkbookPayload,
  ImportedWorkbook,
  CreateWorkbookSharePayload,
  PublicWorkbookShareView,
  WorkbookAttachmentPresignResponse,
  WorkbookCloneMode,
  WorkbookComment,
  WorkbookCaptureTemplatePayload,
  WorkbookCaptureTemplateResponse,
  WorkbookDetail,
  WorkbookAccessGrantGroups,
  WorkbookMenuPin,
  WorkbookQuotaSummary,
  WorkbookShare,
  WorkbookSheetDetail,
  WorkbookVersion,
  WorkbooksListResponse,
  UpdateWorkbookAclPayload,
  UpdateWorkbookSheetAclPayload,
  WorkbookFormulaDependenciesResponse,
} from './types';

export async function fetchWorkbookPinTaxonomies(): Promise<
  import('../formTaxonomies/types').FormTaxonomyMenuResponse
> {
  const { data } = await api.get('workbooks/pin-taxonomies');
  return data;
}

export async function fetchWorkbooks(params?: {
  search?: string;
  status?: string;
  page?: number;
}): Promise<WorkbooksListResponse> {
  const { data } = await api.get<WorkbooksListResponse>('/workbooks', { params });
  return data;
}

export async function fetchWorkbook(id: string): Promise<WorkbookDetail> {
  const { data } = await api.get<WorkbookDetail>(`/workbooks/${id}`);
  return data;
}

export async function createWorkbook(payload: CreateWorkbookPayload) {
  const { data } = await api.post(`/workbooks`, payload);
  return data;
}

export async function importWorkbook(payload: ImportWorkbookPayload): Promise<ImportedWorkbook> {
  const { data } = await api.post<ImportedWorkbook>(`/workbooks/import`, payload);
  return data;
}

export async function archiveWorkbook(id: string) {
  const { data } = await api.post(`/workbooks/${id}/archive`);
  return data;
}

export async function deleteWorkbook(id: string) {
  await api.delete(`/workbooks/${id}`);
}

export async function fetchWorkbookSheet(
  workbookId: string,
  sheetId: string
): Promise<WorkbookSheetDetail> {
  const { data } = await api.get<WorkbookSheetDetail>(`/workbooks/${workbookId}/sheets/${sheetId}`);
  return data;
}

export async function saveWorkbookSheetSnapshot(
  workbookId: string,
  sheetId: string,
  snapshot: Record<string, unknown>
) {
  const { data } = await api.put(`/workbooks/${workbookId}/sheets/${sheetId}/snapshot`, {
    snapshot,
  });
  return data;
}

export async function addWorkbookSheet(
  workbookId: string,
  name?: string,
  engineSheetId?: string,
) {
  const { data } = await api.post(`/workbooks/${workbookId}/sheets`, { name, engineSheetId });
  return data;
}

export async function cloneWorkbook(
  workbookId: string,
  payload: { mode: WorkbookCloneMode; name?: string; cloneCaptureTemplate?: boolean }
) {
  const { data } = await api.post(`/workbooks/${workbookId}/clone`, payload);
  return data;
}

export async function fetchWorkbookCaptureTemplate(
  workbookId: string
): Promise<WorkbookCaptureTemplateResponse> {
  const { data } = await api.get<WorkbookCaptureTemplateResponse>(
    `/workbooks/${workbookId}/capture-template`
  );
  return data;
}

export async function saveWorkbookCaptureTemplate(
  workbookId: string,
  payload: WorkbookCaptureTemplatePayload
): Promise<WorkbookCaptureTemplateResponse> {
  const { data } = await api.post<WorkbookCaptureTemplateResponse>(
    `/workbooks/${workbookId}/capture-template`,
    payload
  );
  return data;
}

export async function fetchWorkbookMenuPins(workbookId: string): Promise<WorkbookMenuPin[]> {
  const { data } = await api.get<WorkbookMenuPin[]>(`/workbooks/${workbookId}/menu-pins`);
  return data;
}

export async function createWorkbookMenuPin(
  workbookId: string,
  payload: {
    taxonomyId: string;
    label?: string;
    icon?: string;
    order?: number;
    isFixed?: boolean;
  }
) {
  const { data } = await api.post<WorkbookMenuPin>(`/workbooks/${workbookId}/menu-pins`, payload);
  return data;
}

export async function deleteWorkbookMenuPin(workbookId: string, pinId: string) {
  await api.delete(`/workbooks/${workbookId}/menu-pins/${pinId}`);
}

export async function fetchWorkbookVersions(workbookId: string): Promise<WorkbookVersion[]> {
  const { data } = await api.get<WorkbookVersion[]>(`/workbooks/${workbookId}/versions`);
  return data;
}

export async function publishWorkbookVersion(workbookId: string, label?: string) {
  const { data } = await api.post<WorkbookVersion>(`/workbooks/${workbookId}/versions/publish`, {
    label,
  });
  return data;
}

export async function restoreWorkbookVersion(workbookId: string, versionId: string) {
  const { data } = await api.post(`/workbooks/${workbookId}/versions/${versionId}/restore`);
  return data;
}

export async function fetchWorkbookQuota(workbookId: string): Promise<WorkbookQuotaSummary> {
  const { data } = await api.get<WorkbookQuotaSummary>(`/workbooks/${workbookId}/quota`);
  return data;
}

export async function presignWorkbookAttachment(
  workbookId: string,
  sheetId: string,
  payload: {
    row: number;
    col: number;
    fileName: string;
    mimeType: string;
    sizeBytes: number;
  }
): Promise<WorkbookAttachmentPresignResponse> {
  const { data } = await api.post<WorkbookAttachmentPresignResponse>(
    `/workbooks/${workbookId}/sheets/${sheetId}/attachments/presign`,
    payload
  );
  return data;
}

export async function uploadWorkbookAttachmentBody(
  attachmentId: string,
  file: File
): Promise<void> {
  await api.put(`/workbook-attachments/${attachmentId}/upload`, file, {
    headers: { 'Content-Type': file.type || 'application/octet-stream' },
    timeout: 600_000,
  });
}

export async function confirmWorkbookAttachment(
  workbookId: string,
  sheetId: string,
  attachmentId: string
) {
  const { data } = await api.post(
    `/workbooks/${workbookId}/sheets/${sheetId}/attachments/${attachmentId}/confirm`
  );
  return data;
}

export async function deleteWorkbookAttachment(workbookId: string, attachmentId: string) {
  await api.delete(`/workbooks/${workbookId}/attachments/${attachmentId}`);
}

export async function fetchWorkbookShares(workbookId: string): Promise<WorkbookShare[]> {
  const { data } = await api.get<WorkbookShare[]>(`/workbooks/${workbookId}/shares`);
  return data;
}

export async function fetchWorkbookAccessGrants(
  workbookId: string
): Promise<WorkbookAccessGrantGroups> {
  const { data } = await api.get<{ grants: WorkbookAccessGrantGroups }>(
    `/workbooks/${workbookId}/grants`
  );
  return data.grants;
}

export async function replaceWorkbookAccessGrants(
  workbookId: string,
  grants: WorkbookAccessGrantGroups
): Promise<WorkbookAccessGrantGroups> {
  const { data } = await api.put<{ grants: WorkbookAccessGrantGroups }>(
    `/workbooks/${workbookId}/grants`,
    {
      grants,
    }
  );
  return data.grants;
}

export async function createWorkbookShare(
  workbookId: string,
  payload: CreateWorkbookSharePayload
): Promise<WorkbookShare> {
  const { data } = await api.post<WorkbookShare>(`/workbooks/${workbookId}/shares`, payload);
  return data;
}

export async function revokeWorkbookShare(workbookId: string, shareId: string) {
  await api.delete(`/workbooks/${workbookId}/shares/${shareId}`);
}

export async function fetchPublicWorkbookByToken(token: string): Promise<PublicWorkbookShareView> {
  const { data } = await api.get<PublicWorkbookShareView>(`/share/workbooks/${token}`);
  return data;
}

export async function fetchWorkbookComments(
  workbookId: string,
  sheetId: string,
  params?: { row?: number; col?: number }
): Promise<WorkbookComment[]> {
  const { data } = await api.get<WorkbookComment[]>(
    `/workbooks/${workbookId}/sheets/${sheetId}/comments`,
    { params }
  );
  return data;
}

export async function createWorkbookComment(
  workbookId: string,
  sheetId: string,
  payload: CreateWorkbookCommentPayload
): Promise<WorkbookComment> {
  const { data } = await api.post<WorkbookComment>(
    `/workbooks/${workbookId}/sheets/${sheetId}/comments`,
    payload
  );
  return data;
}

export async function toggleWorkbookCommentResolved(
  workbookId: string,
  commentId: string
): Promise<WorkbookComment> {
  const { data } = await api.post<WorkbookComment>(
    `/workbooks/${workbookId}/comments/${commentId}/resolve`
  );
  return data;
}

export async function deleteWorkbookComment(workbookId: string, commentId: string) {
  await api.delete(`/workbooks/${workbookId}/comments/${commentId}`);
}

export async function updateWorkbookAcl(workbookId: string, payload: UpdateWorkbookAclPayload) {
  const { data } = await api.patch(`/workbooks/${workbookId}`, payload);
  return data;
}

export async function fetchFormulaDependencySnapshots(
  workbookId: string,
  sheetIds: string[],
): Promise<WorkbookFormulaDependenciesResponse> {
  const { data } = await api.post<WorkbookFormulaDependenciesResponse>(
    `/workbooks/${workbookId}/formula-dependencies`,
    { sheetIds },
  );
  return data;
}

export async function updateWorkbookSheetAcl(
  workbookId: string,
  sheetId: string,
  payload: UpdateWorkbookSheetAclPayload,
) {
  const { data } = await api.patch(`/workbooks/${workbookId}/sheets/${sheetId}`, payload);
  return data;
}
